import { BaseApiService, API_CONFIG } from './base.service';
import {
  Server,
  ServerBillingToken,
  ServerCreate,
  ServerDetailsResponse,
  ServerPricelistGroupAssignmentsUpdate,
  ServerUpdate,
  Supervisor,
  SupervisorCreate,
  SupervisorUpdate,
  Controller,
  ControllerCreate,
  ControllerUpdate,
  PaginatedResponse
} from '../../types';

/**
 * Service for managing servers, supervisors, and controllers
 */
export class ServerService extends BaseApiService {
  // Servers
  async getServers(page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<Server>> {
    return this.getPaginated(API_CONFIG.ENDPOINTS.SERVERS, page, pageSize);
  }

  async getAllServers(limit: number = 100): Promise<Server[]> {
    const validLimit = Math.max(1, Math.floor(limit) || 100);
    const response = await this.get<any>(`${API_CONFIG.ENDPOINTS.SERVERS}/?skip=0&limit=${validLimit}`);
    // Handle ServerListResponse object format
    if (response && response.servers && Array.isArray(response.servers)) {
      return response.servers;
    } else if (Array.isArray(response)) {
      // Fallback for direct array response
      return response;
    } else {
      console.warn('getAllServers: Unexpected response format:', response);
      return [];
    }
  }

  async getServer(id: string): Promise<Server> {
    return this.get(`${API_CONFIG.ENDPOINTS.SERVERS}/${id}`);
  }

  async getServerDetails(id: string): Promise<ServerDetailsResponse> {
    return this.get(`${API_CONFIG.ENDPOINTS.SERVERS}/${id}/details`);
  }

  async createServer(server: ServerCreate): Promise<Server> {
    return this.post(`${API_CONFIG.ENDPOINTS.SERVERS}/`, server);
  }

  async updateServer(id: string, server: ServerUpdate): Promise<Server> {
    return this.put(`${API_CONFIG.ENDPOINTS.SERVERS}/${id}`, server);
  }

  async deleteServer(id: string): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.SERVERS}/${id}`);
  }

  async regenerateServerCredentials(id: string): Promise<Server> {
    return this.post(`${API_CONFIG.ENDPOINTS.SERVERS}/${id}/regenerate-credentials`);
  }

  async generateBillingBackendToken(id: string): Promise<ServerBillingToken> {
    return this.post(`${API_CONFIG.ENDPOINTS.SERVERS}/${id}/billing-backend-token`);
  }

  async updateServerPricelistGroups(
    id: string,
    payload: ServerPricelistGroupAssignmentsUpdate,
  ): Promise<ServerDetailsResponse> {
    return this.put(`${API_CONFIG.ENDPOINTS.SERVERS}/${id}/pricelist-groups`, payload);
  }

  async uploadFile(serverId: string, file: File, fileType: 'supervisor' | 'controller'): Promise<{ message: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    const response = await this.axiosInstance.post(`${API_CONFIG.ENDPOINTS.SERVERS}/${serverId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  }

  // Supervisors
  async getSupervisors(page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<Supervisor>> {
    return this.getPaginated(API_CONFIG.ENDPOINTS.SUPERVISORS, page, pageSize);
  }

  async getAllSupervisors(limit: number = 100): Promise<Supervisor[]> {
    const validLimit = Math.max(1, Math.floor(limit) || 100);
    return this.get(`${API_CONFIG.ENDPOINTS.SUPERVISORS}/?skip=0&limit=${validLimit}`);
  }

  async getSupervisor(id: number): Promise<Supervisor> {
    return this.get(`${API_CONFIG.ENDPOINTS.SUPERVISORS}/${id}`);
  }

  async createSupervisor(supervisor: SupervisorCreate): Promise<Supervisor> {
    return this.post(`${API_CONFIG.ENDPOINTS.SUPERVISORS}/`, supervisor);
  }

  async updateSupervisor(id: number, supervisor: SupervisorUpdate): Promise<Supervisor> {
    return this.put(`${API_CONFIG.ENDPOINTS.SUPERVISORS}/${id}`, supervisor);
  }

  async deleteSupervisor(id: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.SUPERVISORS}/${id}`);
  }

  // Controllers
  async getControllers(page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<Controller>> {
    return this.getPaginated(API_CONFIG.ENDPOINTS.CONTROLLERS, page, pageSize);
  }

  async getAllControllers(limit: number = 100): Promise<Controller[]> {
    const validLimit = Math.max(1, Math.floor(limit) || 100);
    return this.get(`${API_CONFIG.ENDPOINTS.CONTROLLERS}/?skip=0&limit=${validLimit}`);
  }

  async getController(id: number): Promise<Controller> {
    return this.get(`${API_CONFIG.ENDPOINTS.CONTROLLERS}/${id}`);
  }

  async getControllersBySupervisor(supervisorId: number): Promise<Controller[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.CONTROLLERS}/?supervisor_id=${supervisorId}`);
  }

  async createController(controller: ControllerCreate): Promise<Controller> {
    return this.post(`${API_CONFIG.ENDPOINTS.CONTROLLERS}/`, controller);
  }

  async updateController(id: number, controller: ControllerUpdate): Promise<Controller> {
    return this.put(`${API_CONFIG.ENDPOINTS.CONTROLLERS}/${id}`, controller);
  }

  async deleteController(id: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.CONTROLLERS}/${id}`);
  }
}

export const serverService = new ServerService();
